import React, { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { makeGetCurrentUser } from 'selectors/users';
import { makeGetRoles } from 'selectors/roles';

import { DEFAULT_PERMISSIONS, CUSTOM_ROLE_DEFAULT_PERMISSIONS, STATIC_ROLES } from 'constants/roles-and-permissions';

export const RolesAndPermissionsContext = React.createContext();

const makeMapStateToProps = () => {
    const getCurrentUser = makeGetCurrentUser();
    const getRoles = makeGetRoles();

    return ({ users, roles }) => {
        return {
            currentUser: getCurrentUser(users),
            allCustomRoles: getRoles(roles.items)
        }
    };
}

export const RolesAndPermissionsProvider = connect(makeMapStateToProps)(({ allCustomRoles, currentUser, children }) => {
    const { t } = useTranslation('user');

    const customRoles = useMemo(() => {
        return (allCustomRoles || []).filter(({ name }) => !STATIC_ROLES.some(role => role.id === name));
    }, [allCustomRoles])

    const permissions = useMemo(() => {
        const result = JSON.parse(JSON.stringify(DEFAULT_PERMISSIONS));

        customRoles.forEach(({ id , permissions }) => {
            [...new Set([...permissions, ...CUSTOM_ROLE_DEFAULT_PERMISSIONS])].forEach(permission => {
                if (result[permission]) {
                    result[permission].push(id);
                }
            });
        });

        return result;
    }, [customRoles]);

    const roles = useMemo(() => {
        return [
            ...STATIC_ROLES,
            ...customRoles
        ];
    }, [customRoles]);

    const getPermissionsForRole = useCallback(roleId => {
        return Object.keys(permissions).filter(key => permissions[key].includes(roleId));
    }, [permissions]);

    const permissionTypeExists = useCallback(permissionType => {
        if (!(permissionType in permissions)) {
            console.error(`Permissions Service: illegal permission type: ${permissionType}`);

            return false;
        }

        return true;
    }, [permissions])

    const getRoleId = useCallback(user => {
        if (!user) {
            return null;
        }

        return STATIC_ROLES.some(r => r.id === user.role) ? user.role : user.role_id;
    }, [roles]);

    const canUser = useCallback((permissionType, customList, user = currentUser) => {
        if (!permissionTypeExists(permissionType)) {
            return false;
        }

        if (!user) {
            return false;
        }

        if (customList) {
            if (typeof customList === 'string') {
                customList = [customList];
            }

            if (permissions[permissionType].includes('custom') && customList.includes(user.id)) {
                return true;
            }
        }

        return permissions[permissionType].includes(getRoleId(user));
    }, [currentUser, permissions, permissionTypeExists]);

    const getRoles = useCallback((sorted) => {
        if (sorted) {
            return roles.sort((r1, r2) => r1.accessLevel - r2.accessLevel);
        }

        return roles;
    }, [roles, permissions]);

    const getRoleIdsForPermission = useCallback(permissionType => {
        return permissions[permissionType] || [];
    }, [permissions]);

    const getRoleLabel = useCallback((roleId, plural) => {
        const role = roles.find(r => r.id === roleId);

        if (role?.default) {
            return t(`roles.${role?.name + (plural ? 's' : '')}`);
        }

        return role?.name || roleId;
    }, [roles]);

    const getRolesListString = useCallback((ids, plural) => {
        if (!ids) {
            return '';
        }

        return ids.filter(Boolean).map(roleId => getRoleLabel(roleId, plural)).join(', ');
    }, [getRoleLabel]);

    const value = useMemo(() => ({
        canUser,
        getRoles,
        getRoleLabel,
        getRoleIdsForPermission,
        getRoleId,
        getRolesListString,
        getPermissionsForRole
    }), [canUser, getRoles, getRoleLabel, getRoleIdsForPermission, getRolesListString, getPermissionsForRole]);

    return (
        <RolesAndPermissionsContext.Provider value={value}>
            { children }
        </RolesAndPermissionsContext.Provider>
    )
});
